import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  Zap,
  HardDrive,
  Check,
  ExternalLink,
  ArrowLeft,
  Server,
  Database,
  Clock,
  Headphones,
  CreditCard,
  MapPin,
} from 'lucide-react';
import { getGameBySlug } from '@/data/gameservers';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema } from '@/lib/seo';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';

const commonFeatures = [
  { icon: Shield, label: 'DDoS Protection', desc: 'Permanently optimized DDoS filters protect your server at all times.' },
  { icon: MapPin, label: 'Frankfurt Location', desc: 'Central European data center for minimal ping across Germany and Europe.' },
  { icon: Zap, label: 'Instant Setup', desc: 'Your server is online automatically within seconds after payment.' },
  { icon: HardDrive, label: 'Full FTP & Console', desc: 'Complete access to your server files and live console at any time.' },
  { icon: Database, label: 'MySQL Database', desc: 'A MySQL database is included with every game server.' },
  { icon: CreditCard, label: 'PrePaid – No Contract', desc: 'No subscription, no minimum term. Stop paying and the server winds down.' },
];

export default function GameserverDetailPage() {
  const { slug } = useParams();
  const game = getGameBySlug(slug);

  if (!game) {
    return (
      <Layout>
        <SEOHead title="Game not found" noindex={true} />
        <div className="section-spacing flex flex-col items-center justify-center text-center">
          <Server className="w-16 h-16 text-muted-foreground mb-6" />
          <h1 className="text-3xl font-black mb-4">Game not found</h1>
          <p className="text-muted-foreground mb-8">This game server page doesn't exist yet.</p>
          <Link to="/gameservers">
            <Button className="bg-primary hover:bg-primary/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to all Gameservers
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const path = `/gameservers/${game.slug}`;
  const productName = game.seoName ?? `${game.name} Server`;
  const titleWithPrice = `${productName} Hosting from €${game.price}`;
  // SEOHead appends " | DeinServerHost" (17 chars) – keep the price only if the title stays ≤ 60
  const seoTitle = titleWithPrice.length <= 43 ? titleWithPrice : `${productName} Hosting`;
  const seoDescription = `From €${game.price}${game.priceUnit ?? ''}: ${game.seoSummary}`;

  return (
    <Layout>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={path}
        schema={[
          productSchema({
            name: productName,
            description: seoDescription,
            path,
            image: game.image,
            offers: [{
              price: game.price,
              period: game.priceUnit?.endsWith('/month') ? 'month' : undefined,
              unitText: game.priceUnit?.slice(1),
            }],
          }),
          breadcrumbSchema([
            { name: 'Gameservers', path: '/gameservers' },
            { name: game.name, path },
          ]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link
              to="/gameservers"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-gameservers"
            >
              <ArrowLeft className="w-4 h-4" />
              All Gameservers
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">

            {/* Left: Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="glass rounded-2xl overflow-hidden flex items-center justify-center p-8 min-h-[300px]"
            >
              <img
                src={game.image}
                alt={game.name}
                className="max-h-64 w-full object-contain"
                loading="eager"
              />
            </motion.div>

            {/* Right: Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {game.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-black mb-3">
                {game.name} <span className="text-gradient">Server</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-6">{game.tagline}</p>

              {/* Price */}
              {game.price ? (
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-sm text-muted-foreground">from</span>
                  <span className="text-5xl font-black text-primary">€{game.price}</span>
                  <span className="text-muted-foreground">{game.priceUnit}</span>
                </div>
              ) : (
                <div className="mb-8">
                  <span className="text-sm text-muted-foreground">Pricing available in our store</span>
                </div>
              )}

              {/* Game-specific highlights */}
              <ul className="space-y-3 mb-8">
                {game.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              {/* Order CTA */}
              <a
                href={game.orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`order-${game.slug}-btn`}
              >
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 glow-hover font-bold uppercase tracking-wider h-14 px-8 w-full sm:w-auto"
                >
                  {game.price ? `Order Now – from €${game.price}` : 'View Pricing & Order'}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass rounded-2xl p-8 mb-16"
          >
            <p className="text-base text-muted-foreground leading-relaxed max-w-3xl">{game.description}</p>
          </motion.div>

          {/* Common Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-8">Everything included</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {commonFeatures.map((f, i) => (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.06 }}
                  className="flex gap-4 p-5 glass rounded-xl border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm mb-1">{f.label}</div>
                    <div className="text-xs text-muted-foreground leading-relaxed">{f.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 glass rounded-2xl"
          >
            <div>
              <h3 className="text-xl font-bold mb-1">Ready to start your {game.name} server?</h3>
              <p className="text-sm text-muted-foreground">PrePaid – no contract – cancel anytime.</p>
            </div>
            <div className="flex gap-4">
              <Link to="/gameservers">
                <Button variant="outline" className="border-primary/30 hover:border-primary">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  All Games
                </Button>
              </Link>
              <a href={game.orderUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-primary hover:bg-primary/90 glow-hover">
                  Order Now
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
